// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect } from 'react';
import { Music, Guitar, Piano, Mic, Drum, Sparkles, PlayCircle, Users, Library } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useT } from '@/context/LocaleContext';
import LanguageSelector from '@/components/LanguageSelector';
import { track } from '@/lib/analytics';

const heroImg = 'https://images.unsplash.com/photo-1579800655570-9a80fe82533b?w=1600';

export default function Landing() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();

  // Capture ?ref= from URL for later use post-login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      try { localStorage.setItem('pending_ref', ref.toUpperCase()); } catch {}
    }
  }, []);

  const handleLogin = () => {
    track('landing_cta_login_click', { logged_in: !!user });
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const cta = user
    ? () => { track('landing_cta_continue_click'); navigate('/dashboard'); }
    : handleLogin;

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Nav */}
      <header className="relative z-20 px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="brand-landing">
          <div className="w-9 h-9 rounded-xl bg-orange-700 flex items-center justify-center"><Music size={18} /></div>
          <div>
            <div className="font-display text-2xl leading-none">Maestro</div>
            <div className="overline text-stone-400">{t('brand.tagline')}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector />
          <button data-testid="header-login-btn" onClick={cta} className="btn-primary text-sm">
            {user ? t('auth.my_space') : t('auth.login')}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 md:px-12 pt-8 md:pt-16 pb-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 rise">
            <div className="overline text-orange-400 mb-4">{t('landing.overline')}</div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-[5.5rem] leading-[0.95] tracking-tight">
              {t('landing.hero1')} <em className="text-orange-500 not-italic">{t('landing.hero_em')}</em> {t('landing.hero2')}
              <br /> {t('landing.hero3')}
            </h1>
            <p className="mt-6 text-stone-300 text-lg max-w-xl leading-relaxed">
              {t('landing.hero_desc')}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="hero-cta-btn" onClick={cta} className="btn-primary flex items-center gap-2">
                <PlayCircle size={18} /> {user ? t('landing.cta_continue') : t('landing.cta_start')}
              </button>
              <a href="#instruments" className="btn-ghost">{t('landing.explore')}</a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-stone-400 text-sm">
              <div><span className="font-display text-3xl text-stone-100">400+</span><br/>chansons</div>
              <div className="w-px h-8 bg-stone-700" />
              <div><span className="font-display text-3xl text-stone-100">6</span><br/>instruments</div>
              <div className="w-px h-8 bg-stone-700" />
              <div><span className="font-display text-3xl text-stone-100">4</span><br/>niveaux</div>
            </div>
          </div>
          <div className="md:col-span-5 rise" style={{ animationDelay: '120ms' }}>
            <div className="relative">
              <img src={heroImg} alt="guitar" className="w-full h-[480px] object-cover rounded-3xl border border-stone-800" />
              <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 w-64">
                <div className="overline text-orange-400">En apprentissage</div>
                <div className="font-display text-2xl">Wonderwall</div>
                <div className="text-stone-400 text-sm">Oasis · Débutant · Guitare</div>
                <div className="mt-2 h-2 rounded-full bg-stone-800 overflow-hidden">
                  <div className="h-full bg-orange-600" style={{ width: '62%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instruments */}
      <section id="instruments" className="relative z-10 px-6 md:px-12 py-20 border-t border-stone-800/60">
        <div className="overline text-stone-400 mb-3">{t('landing.pick_instrument')}</div>
        <h2 className="font-display text-4xl md:text-5xl mb-10 max-w-2xl">{t('landing.each_instrument')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { icon: Guitar, key: 'guitar' },
            { icon: Piano, key: 'piano' },
            { icon: Mic, key: 'voice' },
            { icon: Drum, key: 'drums' },
            { icon: Music, key: 'bass' },
          ].map(({ icon: Icon, key }) => (
            <div key={key} className="card-stone p-6 hover:border-orange-800/60 transition-colors">
              <Icon size={28} className="text-orange-500 mb-4" />
              <div className="font-display text-2xl">{t(`instruments.${key}`)}</div>
              <div className="text-stone-400 text-sm mt-1">{t('landing.beginner_pro')}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 px-6 md:px-12 py-20 border-t border-stone-800/60">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Library, title: 'Bibliothèque riche', text: 'Accords, tabs, paroles et tutos vidéo pour tous les styles.' },
            { icon: Sparkles, title: 'Maestro IA', text: 'Un tuteur personnel qui répond à tes questions musicales, 24/7.' },
            { icon: Users, title: 'Communauté', text: 'Partage tes reprises et progresse avec des musiciens du monde entier.' },
          ].map(({ icon: Icon, title, text }) => (
            <div key={title} className="glass rounded-2xl p-8">
              <Icon size={26} className="text-orange-400 mb-4" />
              <div className="font-display text-2xl mb-2">{title}</div>
              <div className="text-stone-300 leading-relaxed">{text}</div>
            </div>
          ))}
        </div>
        <div className="mt-16 text-center">
          <button data-testid="footer-cta-btn" onClick={cta} className="btn-primary">
            {user ? 'Aller à mon tableau' : 'Créer un compte gratuit'}
          </button>
        </div>
      </section>

      <footer className="relative z-10 px-6 md:px-12 py-10 border-t border-stone-800/60 text-stone-500 text-sm flex justify-between">
        <div>© Maestro · Fait avec passion</div>
        <div>Débutant · Intermédiaire · Avancé · Pro</div>
      </footer>
    </div>
  );
}


// end

